import { useEffect, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Input, Select, useDisclosure, useToast } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';

import AddIcon from '@/assets/AddIcon';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST, COLORS } from '@/common/constants';
import ModalDelete from '@/components/Modals/ModalDelete';
import ModalWarning from '@/components/Modals/ModalWarning';
import TableVessels from '@/components/Tables/TableVessels';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { setDataTotalNewVessel } from '@/redux/slices/notificationSlice';
import { deleteVessel, getListVessel, checkStatusInvoiceByVesselId } from '@/services/vesselService';
import { downloadFile } from '@/services/downloadService';
import { getNewRecordCounterShipOperator, getNewRecordCounterSuperAdmin } from '@/services/newRecordCounterService';

import '../styles/ListPage.css'

const VesselShipOwnerList = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const location = useLocation();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const toast = useToast();
    const shipOwnerId = useAppSelector((state: RootState) => state.account.appId);
    const name = useAppSelector((state: RootState) => state.account.name);
    const dispatch = useAppDispatch();

    const { idShipOwner, currentPageNumberList, currentPageSizeList } = location.state || {};

    const [searchValue, setSearchValue] = useState('');
    const [statusValue, setStatusValue] = useState('');
    const [typeValue, setTypeValue] = useState('');
    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [dataList, setDataList] = useState([] as any);
    const [idDelete, setIdDelete] = useState('');
    const [selectedOnBehalf, setSelectedOnBehalf] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // State for ModalWarning
    const [isWarningOpen, setIsWarningOpen] = useState(false);
    const [warningTitle, setWarningTitle] = useState('Warning');
    const [warningMessage, setWarningMessage] = useState('');

    // Example function to show warning
    const showWarning = (title: string, message: string) => {
        setWarningTitle(title);
        setWarningMessage(message);
        setIsWarningOpen(true);
    };

    const handleNavAdd = () => {
        const path = role === 'super admin' ? '/manage-ship-operator/vessel/add' : '/manage-vessel/add';
        navigate(path, { state: { idShipOwner, onBehalfCompany: selectedOnBehalf } });
    };

    const handleEdit = (id: string) => {
        const path = role === 'super admin' ? '/manage-ship-operator/vessel/edit' : '/manage-vessel/edit';
        navigate(path, { state: { id, idShipOwner } });
    };

    const handleReview = (id: string) => {
        const path = role === 'super admin' ? '/manage-ship-operator/vessel/review' : '/manage-vessel/review';
        navigate(path, { state: { id, idShipOwner } });
    };

    const handleDelete = (id: string) => {
        checkStatusInvoiceByVesselId(id).then((res: any) => {
            if (res?.status === HttpStatusCode.Ok) {
                if (res.data === null) {
                    setIdDelete(id);
                    onOpen();
                    return;
                }
                if (Array.isArray(res.data) && res.data.length > 0) {
                    // Check if any invoice has paymentStatus !== 4
                    const hasActiveInvoice = res.data.some((inv: any) => inv.paymentStatus !== 4);
                    if (hasActiveInvoice) {
                        showWarning('Cannot Delete', 'This vessel has an active invoice and cannot be deleted.');
                        return;
                    }
                }
            } else {
                showWarning('Error', 'Failed to check invoice status for the vessel.');
                return;
            }
        });
    };

    const handleBack = () => {
        navigate('/manage-ship-operator', { state: { currentPageNumberList, currentPageSizeList } })
    }

    const handleConfirmDelete = async () => {
        const response = await deleteVessel({ vesselId: idDelete, modifiedBy: name || 'System' }) as any;

        if (response.status === HttpStatusCode.Ok) {
            toast({ title: 'Vessel deleted.', description: "We've deleted vessel for you.", status: 'success', duration: DURATION_TOAST, isClosable: true });
            getData();
        } else {
            toast({ title: 'Failed to delete vessel.', description: 'Error occurred while deleting the vessel.', status: 'error', duration: DURATION_TOAST, isClosable: true });
        }

        onClose();
    };

    const handleDownload = async (id: string) => {
			await downloadFile(id);
    }

    const setLatestCounterAdmin = async () => {
        const responseCounter = await getNewRecordCounterSuperAdmin() as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewVessel(responseCounter?.data?.vessel || 0));
        }
    }

    const setLatestCounterShipOperator = async () => {
        const responseCounter = await getNewRecordCounterShipOperator(shipOwnerId) as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewVessel(responseCounter?.data?.vessel || 0));
        }
    }

    const getData = async () => {
        const params = {
            id: idShipOwner ?? shipOwnerId,
            search: searchValue,
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            status: statusValue,
            type: typeValue,
        };

        setIsLoading(true);
        const response = await getListVessel(params) as any;
        if (response.status === HttpStatusCode.Ok) {
            setDataList(response.data);
            setTotalPageNumber(response.totalPage);
            setCurrentPageNumber(response.pageNumber);
            setHasNextPage(response.hasNextPage);
            if (role === 'super admin') {
                setLatestCounterAdmin();
            } else {
                setLatestCounterShipOperator();
            }
        } else {
            toast({ title: response?.status, description: response?.message, status: 'error', duration: DURATION_TOAST, isClosable: true });
        }

        setIsLoading(false);
    };

    useEffect(() => {
        if (idShipOwner || shipOwnerId) {
            getData();
        }
    }, [idShipOwner, shipOwnerId]);

    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber]);

    return (
        <>
            <ModalDelete title="Delete Vessel?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <ModalWarning
                isOpen={isWarningOpen}
                onClose={() => setIsWarningOpen(false)}
                title={warningTitle}
                message={warningMessage}
            />
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        {role === 'super admin' && <div className="table-filter-input filter-input-width-1">
                            <Button  className='table-search-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={handleBack}>Back</Button>
                        </div>}
                        <div className="table-filter-input filter-input-width-8">
                            <Input placeholder="Search" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
                        </div>
                        <div className="table-filter-input filter-input-width-4">
                            <Select placeholder="All Status" onChange={(e) => setStatusValue(e.target.value)}>
                                <option value="1">New</option>
                                <option value="2">In Review</option>
                                <option value="3">Active</option>
                                <option value="4">Rejected</option>
                                <option value="5">Inactive</option>
                            </Select>
                        </div>
                        <div className="table-filter-input filter-input-width-4">
                            <Select placeholder="All Type" onChange={(e) => setTypeValue(e.target.value)}>
                                <option value="SIRE">SIRE</option>
                                <option value="Vetting">Vetting</option>
                            </Select>
                        </div>
                        <div>
                            <Button className='table-search-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={() => getData()}>
                                Search
                            </Button>
                        </div>
                        <div className="table-filter-separator"/>
                        <Button className='table-add-button' leftIcon={<AddIcon />} onClick={handleNavAdd} bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} borderRadius={10}>
                            Add Vessel
                        </Button>
                    </div>
                </div>
                <TableVessels
                    dataList={dataList}
                    isLoading={isLoading}
                    handleEdit={handleEdit}
                    handleDelete={handleDelete}
                    handleReview={handleReview}
                    setSelectedOnBehalf={setSelectedOnBehalf}
                    currentPageSize={currentPageSize}
                    handleChangePageSize={setCurrentPageSize}
                    handleChangePageNumber={setCurrentPageNumber}
                    currentPageNumber={currentPageNumber}
                    totalPageNumber={totalPageNumber}
                    hasNextPage={hasNextPage}
                    role={role}
                    handleDownload={handleDownload}
                />
            </div>
        </>
    );
};

export default VesselShipOwnerList;
